import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { Password } from 'primereact/password';

import { useResetPasswordMutation } from '../../../api';
import { selectAuthError, setError, selectResetPasswordStatus, clearResetPasswordStatus } from '../authSlice';
import { useLanguage } from '../../../contexts/LanguageContext';
import LanguageSelector from '../../../layout/LanguageSelector';

const ResetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const error = useSelector(selectAuthError);
  const resetPasswordStatus = useSelector(selectResetPasswordStatus);
  const { th } = useLanguage();
  
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  // URL'den token ve email'i al
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Reset password validation schema with translations
  const ResetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, th('auth.passwordMinLength'))
      .required(th('auth.passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], th('auth.passwordsDoNotMatch'))
      .required(th('auth.confirmPasswordRequired')),
  });

  // Clean up state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(setError(null));
      dispatch(clearResetPasswordStatus());
    };
  }, [dispatch]);

  // Reset password process
  const handleSubmit = async (values) => {
    try {
      // Reset any previous errors
      dispatch(setError(null));

      const resetData = {
        email: email,
        token: token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword
      };

      // API'ye POST isteği gönder
      await resetPassword(resetData).unwrap();
      
      // Başarı durumu slice'da handle ediliyor
    } catch (err) {
      // Hata durumu slice'da handle ediliyor
      console.error('Şifre sıfırlama hatası:', err);
    }
  };

  // Token veya email yoksa hata göster
  if (!token || !email) {
    return (
      <div className="surface-0 flex align-items-center justify-content-center min-h-screen" style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: 24, right: 32, zIndex: 10 }}>
          <LanguageSelector />
        </div>
        <div className="grid w-30rem mx-auto">
          <div className="col-12 md:col-8 lg:col-6 w-full">
            <Card className="shadow-4">
              <div className="text-center mb-5">
                <div className="text-900 text-2xl font-medium mb-3">{th('auth.resetPasswordTitle')}</div>
                <Message 
                  severity="error" 
                  text={th('auth.invalidResetLink')} 
                  className="w-full mb-4" 
                />
                <div className="flex justify-content-center mt-4">
                  <Button 
                    label={th('auth.backToLogin')} 
                    className="p-button-link" 
                    onClick={() => navigate('/login')} 
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-0 flex align-items-center justify-content-center min-h-screen" style={{ position: 'relative' }}>
      {/* Sağ üst köşe için dil seçici */}
      <div style={{ position: 'absolute', top: 24, right: 32, zIndex: 10 }}>
        <LanguageSelector />
      </div>
      <div className="grid w-30rem mx-auto">
        <div className="col-12 md:col-8 lg:col-6 w-full">
          <Card className="shadow-4">
            <div className="text-center mb-5">
              <div className="text-900 text-2xl font-medium mb-3">{th('auth.resetPasswordTitle')}</div>
              <span className="text-600 font-medium">{th('auth.resetPasswordSubtitle')}</span>
            </div>
            
            {resetPasswordStatus === 'success' ? (
              <div className="w-full">
                <Message 
                  severity="success" 
                  text={th('auth.passwordResetSuccess')} 
                  className="w-full mb-4" 
                />
                <div className="flex justify-content-center mt-4">
                  <Button 
                    label={th('auth.backToLogin')} 
                    className="p-button-link" 
                    onClick={() => navigate('/login')} 
                  />
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <Message severity="error" text={error} className="w-full mb-4" />
                )}
                
                <Formik
                  initialValues={{ newPassword: '', confirmPassword: '' }}
                  validationSchema={ResetPasswordSchema}
                  onSubmit={handleSubmit}
                >
                  {({ errors, touched }) => (
                    <Form className="p-fluid">
                      <div className="field mb-4">
                        <span className="p-input-icon-left w-full">
                          <i className="pi pi-lock"></i>
                          <Field name="newPassword">
                            {({ field }) => (
                              <Password
                                id="newPassword"
                                placeholder={th('auth.newPassword')}
                                className={`w-full text-color-secondary surface-50 border-200 ${errors.newPassword && touched.newPassword ? 'p-invalid' : ''}`}
                                aria-describedby="newPassword-help"
                                toggleMask
                                {...field}
                              />
                            )}
                          </Field>
                        </span>
                        {errors.newPassword && touched.newPassword && (
                          <small id="newPassword-help" className="p-error">{errors.newPassword}</small>
                        )}
                      </div>

                      <div className="field mb-4">
                        <span className="p-input-icon-left w-full">
                          <i className="pi pi-lock"></i>
                          <Field name="confirmPassword">
                            {({ field }) => (
                              <Password
                                id="confirmPassword"
                                placeholder={th('auth.confirmPassword')}
                                className={`w-full text-color-secondary surface-50 border-200 ${errors.confirmPassword && touched.confirmPassword ? 'p-invalid' : ''}`}
                                aria-describedby="confirmPassword-help"
                                toggleMask
                                {...field}
                              />
                            )}
                          </Field>
                        </span>
                        {errors.confirmPassword && touched.confirmPassword && (
                          <small id="confirmPassword-help" className="p-error">{errors.confirmPassword}</small>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 justify-content-between">
                        <Button
                          type="button"
                          label={th('auth.cancel')}
                          className="flex-auto p-button-outlined"
                          onClick={() => navigate('/login')}
                        />
                        <Button
                          type="submit"
                          label={isLoading ? th('auth.resetting') : th('auth.resetPassword')}
                          className="flex-auto"
                          disabled={isLoading}
                          icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            )}
            
            <div className="mt-4 text-center">
              <span className="text-600 font-medium">{th('auth.noAccount')}</span>
              <Link to="/register" className="font-medium no-underline ml-2" style={{ color: 'var(--primary-color)' }}>
                {th('auth.register')}
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 